import { addTransportationTypeByFactory, createAgentBank, createAgentBankType, createAgentByFactory, createBankFactory, createRetailBankType, createRetailByFactory, createTransportationAgent, createTransportationConfigs, createPayout, deleteAgentBank, deleteAgentBankType, deleteBankFactory, deleteRetailBankType, deleteTransportationAgent, deleteTransportationTypeByFactory, deleteUserImmediately, getAgentBankList, getAgentBankTypeList, getAgentByTelephone, getAgentListByTelephone, getBankActiveList, getBankAdminList, getBankById, getBankInActiveList, getFactoryById, getLogoBank, getOrderBangbowByFactory, getOrderInProgress, getOrderInProgressSummary, getOrderPendingPayment, getOrderPendingPaymentSummary, getPayoutBatchSummary, getPayoutPendingSummary, getPayoutItems, exportPayoutItems, listPayout, getRetailBankTypeList, getTransportationConfigs, getTransportationConfigsByAgentId, getTransportationType, getTransportationTypeByFactory, loadBankList, loadTransportationAll, UpdateAgentByFactory, updateBankFactory, updateBankFactoryStatus, UpdateRetailByFactory } from 'services/factoryAPI'
import {
  AddSelectTransportationInput,
  AddSelectTransportationResponse,
  createAgentBankInput,
  createAgentBankResponse,
  createAgentBankTypeInput,
  createAgentBankTypeResponse,
  CreateAgentInput,
  CreateAgentResponse,
  CreateBankInput,
  CreateBankResponse,
  CreateGeographyInput,
  createRetailBankTypeInput,
  createRetailBankTypeResponse,
  CreateTransportationAgentInput,
  CreateTransportationAgentResponse,
  CreateTransportationResponse,
  deleteAgentBankInput,
  deleteAgentBankResponse,
  deleteAgentBankTypeInput,
  deleteAgentBankTypeResponse,
  deleteAgentImmediatelyInput,
  deleteAgentImmediatelyResponse,
  DeleteBankInput,
  deleteRetailBankTypeResponse,
  DeleteSelectTransportationInput,
  DeleteSelectTransportationResponse,
  DeleteTransportationAgentInput,
  DeleteTransportationAgentResponse,
  GetAgentBankListResponse,
  GetAgentBankTypeListResponse,
  GetAgentByIdResponse,
  GetAgentListByTelephoneResponse,
  GetBankAllListInput,
  GetBankAllListResponse,
  GetFactoryByIdResponse,
  GetLogoBankResponse,
  GetOrderBangbowByFactoryInput,
  GetOrderBangbowByFactoryResponse,
  GetOrderInProgressInput,
  GetOrderInProgressResponse,
  GetOrderInProgressSummaryInput,
  GetOrderInProgressSummaryResponse,
  GetOrderPendingPaymentSummaryInput,
  GetOrderPendingPaymentSummaryResponse,
  LoadBankListInput,
  LoadBankListResponse,
  loadTransportationAllInput,
  loadTransportationAllResponse,
  LoadTransportationIDInput,
  LoadTransportationResponse,
  TransportationConfigsByAgentIdResponse,
  TransportationTypeResponse,
  TransportTypeSelectResponse,
  UpdateAgentInput,
  UpdateBankStatusInput,
  CreatePayoutInput,
  CreatePayoutResponse,
  GetPayoutPendingSummaryResponse,
  PayoutBatchSummaryResponse,
  PayoutListInput,
  PayoutListResponse,
  PayoutDetailResponse,
} from 'services/interface'
import { getRetailById } from 'services/agentsApi'
import { UseMutationResult, useMutation, UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'

// Create Agent
export const useCreateAgent = (): UseMutationResult<
  CreateAgentResponse,
  Error,
  CreateAgentInput,
  unknown
> => {
  return useMutation<CreateAgentResponse, Error, CreateAgentInput, unknown>({
    mutationFn: (input: CreateAgentInput) => createAgentByFactory(input),
  })
}

// Create Retail
export const useCreateRetail = (): UseMutationResult<
  CreateAgentResponse,
  Error,
  CreateAgentInput,
  unknown
> => {
  return useMutation<CreateAgentResponse, Error, CreateAgentInput, unknown>({
    mutationFn: (input: CreateAgentInput) => createRetailByFactory(input),
  })
}

// Update Agent
export const useUpdateAgent = (): UseMutationResult<
  CreateAgentResponse,
  Error,
  UpdateAgentInput,
  unknown
> => {
  return useMutation<CreateAgentResponse, Error, UpdateAgentInput, unknown>({
    mutationFn: (input: UpdateAgentInput) => UpdateAgentByFactory(input),
  })
}

// Update Retail
export const useUpdateRetail = (): UseMutationResult<
  CreateAgentResponse,
  Error,
  UpdateAgentInput,
  unknown
> => {
  return useMutation<CreateAgentResponse, Error, UpdateAgentInput, unknown>({
    mutationFn: (input: UpdateAgentInput) => UpdateRetailByFactory(input),
  })
}

// Delete User Immediately
export const useDeleteUserImmediately = (): UseMutationResult<
  deleteAgentImmediatelyResponse,
  Error,
  deleteAgentImmediatelyInput,
  unknown
> => {
  return useMutation<deleteAgentImmediatelyResponse, Error, deleteAgentImmediatelyInput, unknown>({
    mutationFn: (input: deleteAgentImmediatelyInput) => deleteUserImmediately(input),
  })
}

// Get Agent List By Telephone
export const useGetAgentListByTelephone = (
  telephone: string
): UseQueryResult<GetAgentListByTelephoneResponse, Error> => {
  return useQuery<GetAgentListByTelephoneResponse, Error>({
    queryKey: ['agentsListTelephone', telephone],
    queryFn: () => getAgentListByTelephone(telephone),
  })
}

// Get Agent By Telephone
export const useGetAgentByTelephone = (
  telephone: string
): UseQueryResult<GetAgentByIdResponse, Error> => {
  return useQuery<GetAgentByIdResponse, Error>({
    queryKey: ['agentsTelephone', telephone],
    queryFn: () => getAgentByTelephone(telephone),
  })
}

// Get Agent By Telephone (Mutation)
export const useGetAgentByTelephoneMutate = (): UseMutationResult<
  GetAgentByIdResponse,
  Error,
  string,
  unknown
> => {
  return useMutation<GetAgentByIdResponse, Error, string, unknown>({
    mutationFn: (input: string) => getAgentByTelephone(input),
  })
}

// Get Bank All List
export const useGetBankAllList = (
  input: GetBankAllListInput
): UseQueryResult<GetBankAllListResponse, Error> => {
  return useQuery<GetBankAllListResponse, Error>({
    queryKey: ['bankAllList', input.ref_id, input.ref_type, input.status],
    queryFn: () => getBankAdminList(input),
  })
}

// Get Bank Active List
export const useGetBankActiveList = (factoryId: string): UseQueryResult<GetBankAllListResponse, Error> => {
  return useQuery<GetBankAllListResponse, Error>({
    queryKey: ['bankActiveList', factoryId],
    queryFn: () => getBankActiveList(factoryId),
  })
}

// Get Bank Inactive List
export const useGetBankInactiveList = (factoryId: string): UseQueryResult<GetBankAllListResponse, Error> => {
  return useQuery<GetBankAllListResponse, Error>({
    queryKey: ['bankInactiveList', factoryId],
    queryFn: () => getBankInActiveList(factoryId),
  })
}


// Get Bank By ID
export const useGetBankById = (
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<GetBankAllListResponse, Error> => {
  return useQuery<GetBankAllListResponse, Error>({
    queryKey: ['bankById', id],
    queryFn: () => getBankById(id),
    enabled: options?.enabled ?? true,
  })
}

// Get Transportation Configs
export const useGetTransportationConfigs = (input: LoadTransportationIDInput): UseQueryResult<LoadTransportationResponse, Error> => {
  return useQuery<LoadTransportationResponse, Error>({
    queryKey: ['transportationConfigsById', input.geography_id, input.transport_type],
    queryFn: () => getTransportationConfigs(input),
  })
}

// Create Bank Factory
export const useCreateBankFactory = (): UseMutationResult<
  CreateBankResponse,
  Error,
  CreateBankInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation<CreateBankResponse, Error, CreateBankInput, unknown>({
    mutationFn: (input: CreateBankInput) => createBankFactory(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['loadBankList'] })
    },
  })
}

// Update Bank Factory
export const useUpdateBankFactory = (): UseMutationResult<
  CreateBankResponse,
  Error,
  CreateBankInput,
  unknown
> => {
  return useMutation<CreateBankResponse, Error, CreateBankInput, unknown>({
    mutationFn: (input: CreateBankInput) => updateBankFactory(input),
  })
}

// Update Bank Factory Status
export const useUpdateBankFactoryStatus = (): UseMutationResult<
  CreateBankResponse,
  Error,
  UpdateBankStatusInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation<CreateBankResponse, Error, UpdateBankStatusInput, unknown>({
    mutationFn: (input: UpdateBankStatusInput) => updateBankFactoryStatus(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['loadBankList'] })
    },
  })
}

// Delete Bank Factory
export const useDeleteBankFactory = (): UseMutationResult<
  CreateBankResponse,
  Error,
  DeleteBankInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation<CreateBankResponse, Error, DeleteBankInput, unknown>({
    mutationFn: (input: DeleteBankInput) => deleteBankFactory(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['loadBankList'] })
    },
  })
}

// Get Logo Bank
export const useGetLogoBank = (): UseQueryResult<GetLogoBankResponse[], Error> => {
  return useQuery<GetLogoBankResponse[], Error>({
    queryKey: ['bankLogo'],
    queryFn: () => getLogoBank(),
  })
}

// Load Transportation All
export const useLoadTransportationAll = (input: loadTransportationAllInput): UseQueryResult<loadTransportationAllResponse, Error> => {
  return useQuery<loadTransportationAllResponse, Error>({
    queryKey: ['loadTransportationAll', input.price_rate, input.service_price, input.search, input.transport_type],
    queryFn: () => loadTransportationAll(input),
  })
}

// Get Transportation Type
export const useGetTransportationType = (): UseQueryResult<TransportationTypeResponse, Error> => {
  return useQuery<TransportationTypeResponse, Error>({
    queryKey: ['transportationType'],
    queryFn: () => getTransportationType(),
  })
}

// Get Transportation Configs By Agent ID
export const useGetTransportationConfigsByAgentId = (input: string): UseQueryResult<TransportationConfigsByAgentIdResponse, Error> => {
  return useQuery<TransportationConfigsByAgentIdResponse, Error>({
    queryKey: ['getTransportationByAgentId', input],
    queryFn: () => getTransportationConfigsByAgentId(input),
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

// Create Transportation Agent
export const useCreateTransportationAgent = (): UseMutationResult<
  CreateTransportationAgentResponse,
  Error,
  CreateTransportationAgentInput,
  unknown
> => {
  return useMutation<CreateTransportationAgentResponse, Error, CreateTransportationAgentInput, unknown>({
    mutationFn: (input: CreateTransportationAgentInput) => createTransportationAgent(input),
  })
}

// Delete Transportation Agent
export const useDeleteTransportationAgent = (): UseMutationResult<
  DeleteTransportationAgentResponse,
  Error,
  DeleteTransportationAgentInput,
  unknown
> => {
  return useMutation<DeleteTransportationAgentResponse, Error, DeleteTransportationAgentInput, unknown>({
    mutationFn: (input: DeleteTransportationAgentInput) => deleteTransportationAgent(input),
  })
}

// Create Transportation Configs
export const useCreateTransportationConfigs = (): UseMutationResult<
  CreateTransportationResponse,
  Error,
  CreateGeographyInput,
  unknown
> => {
  return useMutation<CreateTransportationResponse, Error, CreateGeographyInput, unknown>({
    mutationFn: (input: CreateGeographyInput) => createTransportationConfigs(input),
  })
}

// Load Bank List
export const useLoadBankList = (input: LoadBankListInput): UseQueryResult<LoadBankListResponse, Error> => {
  return useQuery<LoadBankListResponse, Error>({
    queryKey: ['loadBankList', input.bank_id, input.start_date, input.end_date, input.limit, input.offset, input.status],
    queryFn: () => loadBankList(input),
  })
}

// Get Agent Bank List
export const useGetAgentBankList = (input: string): UseQueryResult<GetAgentBankListResponse, Error> => {
  return useQuery<GetAgentBankListResponse, Error>({
    queryKey: ['getAgentBankList', input],
    queryFn: () => getAgentBankList(input),
  })
}

// Create Agent Bank
export const useCreateAgentBank = (): UseMutationResult<
  createAgentBankResponse,
  Error,
  createAgentBankInput,
  unknown
> => {
  return useMutation<createAgentBankResponse, Error, createAgentBankInput, unknown>({
    mutationFn: (input: createAgentBankInput) => createAgentBank(input),
  })
}

// Delete Agent Bank
export const useDeleteAgentBank = (): UseMutationResult<
  deleteAgentBankResponse,
  Error,
  deleteAgentBankInput,
  unknown
> => {
  return useMutation<deleteAgentBankResponse, Error, deleteAgentBankInput, unknown>({
    mutationFn: (input: deleteAgentBankInput) => deleteAgentBank(input),
  })
}

// Get Agent Bank Type List
export const useGetAgentBankTypeList = (input: string): UseQueryResult<GetAgentBankTypeListResponse, Error> => {
  return useQuery<GetAgentBankTypeListResponse, Error>({
    queryKey: ['getAgentBankTypeList', input],
    queryFn: () => getAgentBankTypeList(input),
  })
}

// Get Retail Bank Type List
export const useGetRetailBankTypeList = (input: string): UseQueryResult<GetAgentBankTypeListResponse, Error> => {
  return useQuery<GetAgentBankTypeListResponse, Error>({
    queryKey: ['getRetailBankTypeList', input],
    queryFn: () => getRetailBankTypeList(input),
  })
}

// Create Agent Bank Type
export const useCreateAgentBankType = (): UseMutationResult<
  createAgentBankTypeResponse,
  Error,
  createAgentBankTypeInput,
  unknown
> => {
  return useMutation<createAgentBankTypeResponse, Error, createAgentBankTypeInput, unknown>({
    mutationFn: (input: createAgentBankTypeInput) => createAgentBankType(input),
  })
}

// Delete Agent Bank Type
export const useDeleteAgentBankType = (): UseMutationResult<
  deleteAgentBankTypeResponse,
  Error,
  deleteAgentBankTypeInput,
  unknown
> => {
  return useMutation<deleteAgentBankTypeResponse, Error, deleteAgentBankTypeInput, unknown>({
    mutationFn: (input: deleteAgentBankTypeInput) => deleteAgentBankType(input),
  })
}

// Create Retail Bank Type
export const useCreateRetailBankType = (): UseMutationResult<
  createRetailBankTypeResponse,
  Error,
  createRetailBankTypeInput,
  unknown
> => {
  return useMutation<createRetailBankTypeResponse, Error, createRetailBankTypeInput, unknown>({
    mutationFn: (input: createRetailBankTypeInput) => createRetailBankType(input),
  })
}

// Delete Retail Bank Type
export const useDeleteRetailBankType = (): UseMutationResult<
  deleteRetailBankTypeResponse,
  Error,
  createRetailBankTypeInput,
  unknown
> => {
  return useMutation<deleteRetailBankTypeResponse, Error, createRetailBankTypeInput, unknown>({
    mutationFn: (input: createRetailBankTypeInput) => deleteRetailBankType(input),
  })
}
// Get Retail By ID
export const useGetRetailById = (retailId: string): UseQueryResult<GetAgentByIdResponse, Error> => {
  return useQuery<GetAgentByIdResponse, Error>({
    queryKey: ['retails', retailId],
    queryFn: () => getRetailById(retailId),
  })
}

// Get Retail Data By ID (mutation)
export const useGetRetailDataById = (): UseMutationResult<GetAgentByIdResponse, Error, string, unknown> => {
  return useMutation<GetAgentByIdResponse, Error, string, unknown>({
    mutationFn: (input: string) => getRetailById(input),
  })
}


///transportation/config/factory_transport_type/{factory_id}
export const useGetTransportationTypeByFactory = (
  factoryId : string
): UseQueryResult<TransportTypeSelectResponse, Error> => {
  return useQuery<TransportTypeSelectResponse, Error>({
    queryKey: ['factory_transport_type', factoryId],
    queryFn: () => getTransportationTypeByFactory(factoryId),
  })
}

///transportation/config/add_factory_transport
export const useAddTransportationTypeByFactory = (): UseMutationResult<
AddSelectTransportationResponse,
  Error,
  AddSelectTransportationInput,
  unknown
> => {
  return useMutation<AddSelectTransportationResponse, Error, AddSelectTransportationInput, unknown>({
    mutationFn: (input: AddSelectTransportationInput) => addTransportationTypeByFactory(input),
  })
}

///transportation/config/add_factory_transport
export const useDeleteTransportationTypeByFactory = (): UseMutationResult<
DeleteSelectTransportationResponse,
  Error,
  DeleteSelectTransportationInput,
  unknown
> => {
  return useMutation<DeleteSelectTransportationResponse, Error, DeleteSelectTransportationInput, unknown>({
    mutationFn: (input: DeleteSelectTransportationInput) => deleteTransportationTypeByFactory(input),
  })
}

export const useGetBankAdminList = (): UseMutationResult<
GetBankAllListResponse,
  Error,
  GetBankAllListInput,
  unknown
> => {
  return useMutation<GetBankAllListResponse, Error, GetBankAllListInput, unknown>({
    mutationFn: (input: GetBankAllListInput) => getBankAdminList(input),
  })
}

//factory/report/order_by_factory useQuery
export const useGetOrderBangbowByFactory = (input: GetOrderBangbowByFactoryInput): UseQueryResult<GetOrderBangbowByFactoryResponse, Error> => {
  return useQuery<GetOrderBangbowByFactoryResponse, Error>({
    queryKey: ['orderBangbowByFactory', input.start_date, input.end_date],
    queryFn: () => getOrderBangbowByFactory(input),
  })
}

//factory/report/order_in_progress useQuery
export const useGetOrderInProgress = (input: GetOrderInProgressInput): UseQueryResult<GetOrderInProgressResponse, Error> => {
  return useQuery<GetOrderInProgressResponse, Error>({
    queryKey: ['orderInProgress', input.start_date, input.end_date, input.page_number, input.row_per_page, input.sort_by, input.sort_direction],
    queryFn: () => getOrderInProgress(input),
  })
}

//factory/report/order_pending_payment useQuery
export const useGetOrderPendingPayment = (input: GetOrderInProgressInput): UseQueryResult<GetOrderInProgressResponse, Error> => {
  return useQuery<GetOrderInProgressResponse, Error>({
    queryKey: ['orderPendingPayment', input.start_date, input.end_date, input.page_number, input.row_per_page, input.sort_by, input.sort_direction],
    queryFn: () => getOrderPendingPayment(input),
  })
}

//factory/report/order_pending_payment_summary useQuery
export const useGetOrderPendingPaymentSummary = (input: GetOrderPendingPaymentSummaryInput): UseQueryResult<GetOrderPendingPaymentSummaryResponse, Error> => {
  return useQuery<GetOrderPendingPaymentSummaryResponse, Error>({
    queryKey: ['orderPendingPaymentSummary', input.start_date, input.end_date],
    queryFn: () => getOrderPendingPaymentSummary(input),
  })
}

//factory/report/order_in_progress_summary useQuery
export const useGetOrderInProgressSummary = (input: GetOrderInProgressSummaryInput): UseQueryResult<GetOrderInProgressSummaryResponse, Error> => {
  return useQuery<GetOrderInProgressSummaryResponse, Error>({
    queryKey: ['orderInProgressSummary', input.start_date, input.end_date],
    queryFn: () => getOrderInProgressSummary(input),
  })
}

// Get Factory By ID
export const useGetFactoryById = (factoryId: string, includeProduct: number = 0): UseQueryResult<GetFactoryByIdResponse, Error> => {
  return useQuery<GetFactoryByIdResponse, Error>({
    queryKey: ['factoryById', factoryId, includeProduct],
    queryFn: () => getFactoryById(factoryId, includeProduct),
    enabled: !!factoryId, // Only fetch if factoryId is provided
  })
}

// Payout pending summary (items not yet in a batch)
export const useGetPayoutPendingSummary = (): UseQueryResult<GetPayoutPendingSummaryResponse, Error> => {
  return useQuery<GetPayoutPendingSummaryResponse, Error>({
    queryKey: ['payoutPendingSummary'],
    queryFn: () => getPayoutPendingSummary(),
  })
}

// Create payout batch
export const useCreatePayout = (): UseMutationResult<CreatePayoutResponse, Error, CreatePayoutInput, unknown> => {
  return useMutation<CreatePayoutResponse, Error, CreatePayoutInput, unknown>({
    mutationFn: (input: CreatePayoutInput) => createPayout(input),
  })
}

// Payout batch summary (PENDING and PAID totals)
export const useGetPayoutBatchSummary = (): UseQueryResult<PayoutBatchSummaryResponse, Error> => {
  return useQuery<PayoutBatchSummaryResponse, Error>({
    queryKey: ['payoutBatchSummary'],
    queryFn: () => getPayoutBatchSummary(),
  })
}

// Payout batch list
export const useGetPayoutList = (input: PayoutListInput): UseQueryResult<PayoutListResponse, Error> => {
  return useQuery<PayoutListResponse, Error>({
    queryKey: ['payoutList', input],
    queryFn: () => listPayout(input),
  })
}

// GET /factory/report/payout/:id/items
export const useGetPayoutItems = (id: string | null): UseQueryResult<PayoutDetailResponse, Error> => {
  return useQuery<PayoutDetailResponse, Error>({
    queryKey: ['payoutItems', id],
    queryFn: () => getPayoutItems(id!),
    enabled: !!id,
  })
}

// Export Excel /factory/report/payout/:id/items
export const useExportPayoutItems = (): UseMutationResult<void, Error, string, unknown> => {
  return useMutation<void, Error, string, unknown>({
    mutationFn: (id: string) => exportPayoutItems(id),
  })
}
